// 261012 E1-P4 (D8–D10) — danh sách "Người phụ không tên" theo tập ở chặng ⑤. MỘT instance cho cả chặng (ScriptStage tạo, truyền xuống panel + thanh hàng loạt)
// để khỏi tải/poll job hai lần.
//  - Tải `GET visual-extras/:storyId` khi mount và mỗi khi `dataVersion` đổi; lỗi ⇒ `loadFailed`, KHÔNG làm hỏng chặng.
//  - Chạy AI: `POST` → `jobId` → `useFilmJobPoll`; sau tải lại trang NHẬN NUÔI job `FILM_VISUAL_EXTRAS` mà BE báo đang chạy. Xong ⇒ toast (kèm chi phí) + `refreshAll()`.
//  - MỌI `POST` đi qua `planRun` → `runConfirmCopy` (hộp ước giá; 261015: không còn hộp "truyện thật").
//  - Ghi (thêm / sửa / Giữ / xoá) = PATCH có `baseVersion`; 200 dán thẳng `{episode, version, items}` vào summary (không đụng outline/kịch bản nên không cần `refreshAll`).
// Logic thuần nằm ở `visualExtrasView.ts` (test được dưới jest); file này chỉ nối API + antd.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { message } from 'antd';
import { adaptationApi } from '../../../services/api/adaptation';
import type {
  VisualExtrasEditBody, VisualExtrasEpisodeView, VisualExtrasJobResult, VisualExtrasLimits, VisualExtrasSummary,
} from '../../../types/adaptation';
import type { JobStepProgress, WorkbenchHandoffMark } from '../../../types/adaptationWorkbench';
import { useFilmJobPoll } from '../useFilmJobPoll';
import { jobFailedToast } from './jobErrorText';
import { formatJobStepProgress } from './jobStepProgress';
import { useAdoptedJobId } from './useAdoptedJobId';
import {
  afterSaveHint, applyEditResult, extrasErrorOf, jobDoneIsPartial, jobDoneText, missingEpisodes, pickExtrasJobId, planRun,
} from './visualExtrasView';
import { useWorkbenchCtx } from './WorkbenchContext';
import type { DangerCopyView } from './workbenchView';

interface JobDetail { progress?: JobStepProgress | number; result?: VisualExtrasJobResult; error?: string; note?: string }

/** Kết quả một lần ghi cho panel: `closeForm` ⇒ đóng ô sửa; `conflict` ⇒ 409 `VERSION_CHANGED` (giữ bản nháp, panel rebase `baseVersion` khi bản mới về). */
export interface ExtrasWriteOutcome { closeForm: boolean; conflict?: boolean }

export interface VisualExtrasApi {
  summary: VisualExtrasSummary | null;
  loadFailed: boolean;
  reload: () => void;
  /** Job đang chạy (của phiên này hoặc BE báo) HOẶC đang gọi POST. */
  jobRunning: boolean;
  /** Đang PATCH / POST. */
  working: boolean;
  progressText: string | null;
  limits: VisualExtrasLimits | null;
  /** Lỗi của job gần nhất ("Không thể đề xuất người phụ: …"); xoá khi chạy lại / chạy xong. */
  runError: string | null;
  /** Giá ĐO mỗi tập của lần chạy trước trong phiên này; `null` ⇒ UI dùng hằng đo 13/10. */
  /** Tập có kịch bản mà chưa từng có danh sách (nút hàng loạt). */
  missing: number[];
  episodeView: (ep: number) => VisualExtrasEpisodeView | null;
  /** Dấu handoff của tập (dữ liệu có sẵn ở `workbench`) — chữ nhắc sau khi lưu. */
  hintFor: (ep: number) => string;
  /** Hỏi xác nhận (ước giá) rồi mới POST. `one` bắt buộc có `episode`. */
  requestRun: (scope: 'missing' | 'one', episode?: number) => void;
  add: (ep: number, label: string, text: string, baseVersion: string) => Promise<ExtrasWriteOutcome>;
  edit: (ep: number, id: string, label: string, text: string, baseVersion: string) => Promise<ExtrasWriteOutcome>;
  setLock: (ep: number, id: string, locked: boolean, baseVersion: string) => Promise<ExtrasWriteOutcome>;
  /** Có hộp hỏi lại trước khi xoá. */
  requestRemove: (ep: number, id: string, label: string, baseVersion: string) => void;
}

export interface VisualExtrasDeps {
  confirmCopy: (c: DangerCopyView, run: () => void | Promise<void>) => void;
}

export const useVisualExtras = ({ confirmCopy }: VisualExtrasDeps): VisualExtrasApi => {
  const { storyId, dataVersion, refreshAll, workbench } = useWorkbenchCtx();
  const [summary, setSummary] = useState<VisualExtrasSummary | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [retry, setRetry] = useState(0);
  const [starting, setStarting] = useState(false);
  const [working, setWorking] = useState(false);
  const [progressText, setProgressText] = useState<string | null>(null);
  const [runError, setRunError] = useState<string | null>(null);
  const currentStoryIdRef = useRef(storyId);
  const inflight = useRef(false);
  currentStoryIdRef.current = storyId;

  useEffect(() => { setSummary(null); setLoadFailed(false); setProgressText(null); setRunError(null); }, [storyId]);
  useEffect(() => {
    let alive = true;
    const forStory = storyId;
    adaptationApi.getVisualExtras(forStory)
      .then((s) => { if (alive && currentStoryIdRef.current === forStory) { setSummary(s); setLoadFailed(false); } })
      .catch(() => { if (alive && currentStoryIdRef.current === forStory) setLoadFailed(true); });
    return () => { alive = false; };
  }, [storyId, dataVersion, retry]);

  const reload = useCallback(() => setRetry((n) => n + 1), []);

  // ── job nền ──
  const { jobId, setJobId, release } = useAdoptedJobId(storyId, pickExtrasJobId(summary?.runningJobs));
  useFilmJobPoll<JobDetail>(jobId, {
    onTick: (task) => {
      const p = task.detail?.progress;
      setProgressText(p && typeof p === 'object' ? formatJobStepProgress(p) || null : null);
    },
    onTerminal: async (outcome, task) => {
      if (outcome === 'completed') {
        const r = task?.detail?.result;
        if (jobDoneIsPartial(r)) message.warning(jobDoneText(r), 8); else message.success(jobDoneText(r), 6);
        setRunError(null);
      } else if (outcome === 'failed') {
        const text = jobFailedToast(task?.detail?.error, task?.detail?.note);
        message.error(text, 6);
        setRunError(`Không thể đề xuất người phụ: ${text}`);
      } else {
        message.warning('Đã dừng theo dõi — việc đề xuất người phụ có thể vẫn chạy, bấm Tải lại.');
      }
      release();
      setProgressText(null);
      if (outcome === 'completed' || outcome === 'failed') await refreshAll(); else reload();
    },
  });
  const jobRunning = !!jobId || starting;

  const limits = summary?.limits ?? null;
  const missing = useMemo(() => missingEpisodes(summary), [summary]);
  const byEp = useMemo(() => new Map((summary?.episodes ?? []).map((e) => [e.episode, e])), [summary]);
  const episodeView = useCallback((ep: number) => byEp.get(ep) ?? null, [byEp]);
  const wbEpisodes = workbench?.episodes;
  const hintFor = useCallback((ep: number) => {
    const mark: WorkbenchHandoffMark | undefined = wbEpisodes?.find((e) => e.ep === ep)?.handoff;
    return afterSaveHint(mark);
  }, [wbEpisodes]);

  // ── chạy AI (tốn tiền) ──
  const startRun = useCallback(async (body: Parameters<typeof adaptationApi.runVisualExtras>[0]) => {
    if (inflight.current) return;
    inflight.current = true;
    setWorking(true);
    setStarting(true);
    setRunError(null);
    try {
      const r = await adaptationApi.runVisualExtras(body);
      if (!r?.jobId) {
        message.warning('Server không trả job id; đã dừng theo dõi và không tự gửi lại.');
        await refreshAll();
        return;
      }
      setJobId(r.jobId);
      message.info('Đề xuất người phụ đã bắt đầu ở nền', 5);
    } catch (e) {
      const d = extrasErrorOf(e, limits);
      message.error(d.text, 6);
      if (d.reload) await refreshAll();
    } finally {
      setStarting(false);
      inflight.current = false;
      setWorking(false);
    }
  }, [setJobId, refreshAll, limits]);

  const requestRun = useCallback((scope: 'missing' | 'one', episode?: number) => {
    if (inflight.current || jobRunning) return;
    let episodes: number[];
    let hasAi = false;
    if (scope === 'one') {
      if (episode === undefined) return;
      episodes = [episode];
      hasAi = (byEp.get(episode)?.items ?? []).some((i) => i.status === 'AI_PROPOSED');
    } else {
      episodes = missing;
    }
    if (!episodes.length) return;
    const plan = planRun({ storyId, scope, episodes, hasAi });
    const chain = (i: number): void | Promise<void> => (i >= plan.steps.length ? startRun(plan.body) : confirmCopy(plan.steps[i], () => chain(i + 1)));
    chain(0);
  }, [jobRunning, byEp, missing, storyId, startRun, confirmCopy]);

  // ── ghi (thêm / sửa / Giữ / xoá) ──
  const patch = useCallback(async (body: VisualExtrasEditBody, okMsg?: string): Promise<ExtrasWriteOutcome> => {
    if (inflight.current) return { closeForm: false };
    inflight.current = true;
    setWorking(true);
    try {
      const r = await adaptationApi.patchVisualExtra(body);
      setSummary((s) => applyEditResult(s, r));
      if (okMsg) message.success(`${okMsg}. ${hintFor(body.episode)}`, 7);
      return { closeForm: true };
    } catch (e) {
      const d = extrasErrorOf(e, limits);
      message.error(d.text, 6);
      if (d.fresh) setSummary((s) => applyEditResult(s, d.fresh as NonNullable<typeof d.fresh>));
      if (d.reload) reload();
      return { closeForm: !d.keepForm, ...(d.code === 'VERSION_CHANGED' ? { conflict: true } : {}) };
    } finally {
      inflight.current = false;
      setWorking(false);
    }
  }, [limits, hintFor, reload]);

  const add = useCallback((ep: number, label: string, text: string, baseVersion: string) =>
    patch({ storyId, episode: ep, op: 'add', label, text, baseVersion }, 'Đã thêm — ghi là “Đã sửa tay” và Giữ'),
  [patch, storyId]);
  const edit = useCallback((ep: number, id: string, label: string, text: string, baseVersion: string) =>
    patch({ storyId, episode: ep, op: 'edit', id, label, text, baseVersion }, 'Đã lưu — ghi là “Đã sửa tay” và Giữ'),
  [patch, storyId]);
  const setLock = useCallback((ep: number, id: string, locked: boolean, baseVersion: string) =>
    patch({ storyId, episode: ep, op: 'setLock', id, locked, baseVersion }),
  [patch, storyId]);
  const requestRemove = useCallback((ep: number, id: string, label: string, baseVersion: string) => {
    if (inflight.current) return;
    confirmCopy({
      title: `Xoá người phụ “${label}”?`, danger: true, okText: 'Xoá',
      content: 'Khối nhân vật của Film sẽ không còn dòng này. Muốn có lại thì bấm “Quét lại” hoặc thêm tay.',
    }, async () => { await patch({ storyId, episode: ep, op: 'remove', id, baseVersion }, 'Đã xoá'); });
  }, [confirmCopy, patch, storyId]);

  return {
    summary, loadFailed, reload, jobRunning, working, progressText, limits, runError, missing,
    episodeView, hintFor, requestRun, add, edit, setLock, requestRemove,
  };
};
